/**
 * THE `?state=` CODEC (launch-marketing-pages design M2).
 *
 * The one encoding of a workspace state tree on the address: the defined keys
 * as compact JSON, then base64. The unified router's `compressState` and
 * `decompressState` delegate here and keep their timing and their default-state
 * fallback; a page outside the workspace (a sign-in landing, a Stripe return
 * URL, a deep-link reader) encodes and decodes through the same two functions
 * without reaching the store. It imports nothing from the app, so every one of
 * those can import it without a cycle (the `services/sessionToken.js` rule).
 */
/* global Buffer */
/* global process */

/**
 * Encoder-side pruning for `encodeState` (th-2a458).
 *
 * The URL carries EXPLICIT choices, so ABSENCE and PRESENCE mean different
 * things to every decoder of the tree: an absent key means "take the default",
 * a key present with a value means "this value" (unifiedRouter design D5,
 * founder ruling 13). `null` IS a value on that contract — an "Any" date
 * bucket, a cleared single-select — so it rides the shared link. Only
 * `undefined`, which is a key the state never set, is dropped.
 *
 * Two arms are unchanged: `undefined` array items are filtered out (an array's
 * own length is meaningful, and a hole is not a choice), and an object that
 * prunes to EMPTY is dropped entirely, since a section with no keys carries no
 * news and only lengthens the URL. An object whose keys are all `null` is NOT
 * empty under this rule and survives, which is the point.
 *
 * @param {*} obj
 * @returns {*} the value with every `undefined` pruned, or `undefined`
 */
export function dropUndefinedValues(obj) {
  if (obj === undefined) return undefined
  if (obj === null) return null

  if (Array.isArray(obj)) {
    return obj.map(dropUndefinedValues).filter(item => item !== undefined)
  }

  if (typeof obj === 'object') {
    const cleaned = {}
    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = dropUndefinedValues(value)
      if (cleanedValue !== undefined) {
        cleaned[key] = cleanedValue
      }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined
  }

  return obj
}

/**
 * A state tree as the `?state=` param: `dropUndefinedValues`, compact JSON,
 * base64. A tree that cannot be encoded (one that prunes to nothing, a cycle)
 * answers `''`.
 *
 * @param {Object} tree - a workspace state tree, whole or partial.
 * @returns {string} the param.
 */
export function encodeState(tree) {
  try {
    // Drop the keys the state never set, keep the ones it set to `null`
    // (see `dropUndefinedValues` — `null` is an explicit choice, not absence).
    const jsonString = JSON.stringify(dropUndefinedValues(tree), null, 0)

    // Use Buffer for Node.js environments (testing) and btoa for browsers
    if (typeof Buffer !== 'undefined' && typeof process !== 'undefined' && process.versions && process.versions.node) {
      // Node.js environment - use Buffer (more reliable for test environment)
      return Buffer.from(jsonString).toString('base64')
    } else if (typeof btoa !== 'undefined') {
      // Browser environment - use btoa
      return btoa(jsonString)
    } else if (typeof Buffer !== 'undefined') {
      // Fallback to Buffer if available
      return Buffer.from(jsonString).toString('base64')
    }
    return jsonString
  } catch (error) {
    console.warn('Failed to compress state:', error)
    return ''
  }
}

/**
 * A `?state=` param as its tree: the object it encodes, or null when the param
 * is empty, is not base64 JSON, or names something other than an object. The
 * tree is raw: the unified router validates it (`validateState`) before any
 * section reads it.
 *
 * @param {string} param - the param as the address carries it.
 * @returns {Object|null}
 */
export function decodeState(param) {
  if (!param || typeof param !== 'string') return null

  try {
    let json

    // For Node.js environments (testing), prefer Buffer over atob
    if (typeof Buffer !== 'undefined' && typeof process !== 'undefined' && process.versions && process.versions.node) {
      json = Buffer.from(param, 'base64').toString('utf8')
    } else if (typeof atob !== 'undefined') {
      json = atob(param)
    } else if (typeof Buffer !== 'undefined') {
      json = Buffer.from(param, 'base64').toString('utf8')
    } else {
      // Final fallback: assume it's already decompressed JSON
      json = param
    }

    const tree = JSON.parse(json)
    return tree && typeof tree === 'object' && !Array.isArray(tree) ? tree : null
  } catch {
    return null
  }
}
